/**
 * Swipe mode page for manual site review
 * Similar to web_ui.html swipe functionality
 * 
 * Features:
 * - Preloading: Next 10 sources are preloaded in hidden iframes to improve UX
 * - No "Checking availability" or "Loading site..." messages for preloaded sources
 * - Automatic single source loading: after each swipe action, 1 new source is loaded in background
 * - This ensures constant pool of 10 preloaded sources ahead at all times
 * 
 * Loading logic:
 * 1. Initial load: batch of 10 sources
 * 2. User swipes (like/dislike/uncertain/skip): load 1 more source immediately
 * 3. Pool always maintains 10 sources ahead of current position
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { isEmbedded } from '@/utils/embed'
import {
  Alert,
  Button,
  Card,
  Space,
  Typography,
  message,
  Row,
  Col,
  Input,
  Switch,
  Modal,
  Segmented,
  Tag,
} from 'antd'
import {
  LeftOutlined,
  ReloadOutlined,
  UndoOutlined,
  ClearOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  PauseCircleOutlined,
  PhoneOutlined,
  GlobalOutlined,
  LinkOutlined,
} from '@ant-design/icons'
import api from '@/services/api'
import { useAuthStore } from '@/stores/authStore'

const { Title, Text, Link } = Typography

interface Source {
  id: number
  project_id: number
  vdl_id: number | null
  source: string
  source_type: string
  subsource: string | null
  source_from: string | null
  status: string
  will_work: boolean
  ai_score: number | null
  ai_comment: string | null
  locked_by_operator_id: number | null
  locked_at: string | null
  created_at: string
  pending_phones: string[] | null
}

interface ParsedPage {
  id: number
  project_id: number
  full_url: string
  domain: string
  path: string | null
  total_clicks: number | null
  queries_count: number | null
  average_position: number | null
  source_status: string
  review_status: string | null
  ai_score: number | null
  ai_recommendation: string | null
  locked_by_operator_id: number | null
  locked_at: string | null
}

// Unified item type for swipe mode
type SwipeItem = {
  itemType: 'source' | 'page'
  id: number
  displayUrl: string
  domain: string
  status: string
  locked_by_operator_id: number | null
  locked_at: string | null
  originalSource?: Source
  originalPage?: ParsedPage
}

type ReviewMode = 'domains' | 'pages'

interface SwipeHistoryEntry {
  item: SwipeItem
  action: 'approved' | 'rejected' | 'uncertain' | 'skipped'
  timestamp: number
}

function SwipeModePage() {
  const { projectId } = useParams<{ projectId: string }>()
  // * When embedded in an iframe (VDL) — hide "exit to project" controls
  const embedded = isEmbedded()
  const navigate = useNavigate()
  const { operator } = useAuthStore()

  // * Review mode: domains (sources from VDL) or pages (ParsedURLs)
  const [reviewMode, setReviewMode] = useState<ReviewMode>('domains')

  // * Unified items list
  const [items, setItems] = useState<SwipeItem[]>([])
  const [sources, setSources] = useState<Source[]>([])
  const [pages, setPages] = useState<ParsedPage[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [totalAvailableSources, setTotalAvailableSources] = useState<number>(0)
  const [totalAvailablePages, setTotalAvailablePages] = useState<number>(0)
  const [loading, setLoading] = useState(false)
  const [loadingBatch, setLoadingBatch] = useState(false) // * Background batch loading
  const [iframeLoaded, setIframeLoaded] = useState(false)
  const [iframeError, setIframeError] = useState(false)
  const [pageTitle, setPageTitle] = useState<string | null>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const currentItemIdRef = useRef<number | null>(null)
  const [swipeStats, setSwipeStats] = useState({
    approved: 0,
    rejected: 0,
    uncertain: 0,
    skipped: 0,
  })
  const [swipeHistory, setSwipeHistory] = useState<SwipeHistoryEntry[]>([])
  const [isProcessingSwipe, setIsProcessingSwipe] = useState(false)

  // * Track skipped items to avoid showing them again in this session
  const [skippedItemIds, setSkippedItemIds] = useState<Set<string>>(new Set()) // "source_123" or "page_456"
  
  // * Track swipe count to reduce API calls for total count updates
  const [swipeCountSinceLastTotalUpdate, setSwipeCountSinceLastTotalUpdate] = useState(0)
  
  // * Keyword highlighting
  const [highlightKeywords, setHighlightKeywords] = useState(false)
  const [keywords, setKeywords] = useState('')
  
  // * Project status popup (shown once on mount)
  const [projectInfo, setProjectInfo] = useState<{
    id: number
    crm_id: number
    project_name: string
    status: string
    parse_phones: boolean
    parse_domains: boolean
  } | null>(null)
  const [statusModalVisible, setStatusModalVisible] = useState(false)

  // * Rejection modal
  const [rejectModalVisible, setRejectModalVisible] = useState(false)
  const [pendingRejection, setPendingRejection] = useState<SwipeItem | null>(null)
  const [customReasonMode, setCustomReasonMode] = useState(false)
  const [customReason, setCustomReason] = useState('')
  
  // * VDL TZ (Technical Specification)
  const [vdlTz, setVdlTz] = useState<string | null>(null)
  
  // * Iframe blocking check
  const [iframeCheckCache, setIframeCheckCache] = useState<Record<string, { embeddable: boolean; reason?: string }>>({})
  const [checkingIframe, setCheckingIframe] = useState(false)
  const [forceProxy, setForceProxy] = useState(false)
  const [isolatedMode, setIsolatedMode] = useState(() => {
    // * Load from localStorage, default to true (safer - no cookies saved)
    const saved = localStorage.getItem('swipe_isolated_mode')
    return saved !== null ? saved === 'true' : true
  })
  
  // * Preloading state for next sources
  const [preloadedIframes, setPreloadedIframes] = useState<Record<number, {
    loaded: boolean;
    error: boolean;
    blocked: boolean;
    blockReason?: string;
    url: string;
    title?: string;
  }>>({})
  const PRELOAD_COUNT = 10 // * Number of sources to preload ahead

  // * Convert Source to SwipeItem
  const sourceToSwipeItem = useCallback((source: Source): SwipeItem => ({
    itemType: 'source',
    id: source.id,
    displayUrl: source.source,
    domain: source.source,
    status: source.status,
    locked_by_operator_id: source.locked_by_operator_id,
    locked_at: source.locked_at,
    originalSource: source,
  }), [])

  // * Convert ParsedPage to SwipeItem
  const pageToSwipeItem = useCallback((page: ParsedPage): SwipeItem => ({
    itemType: 'page',
    id: page.id,
    displayUrl: page.full_url,
    domain: page.domain,
    status: page.source_status,
    locked_by_operator_id: page.locked_by_operator_id,
    locked_at: page.locked_at,
    originalPage: page,
  }), [])

  // * Build items list based on review mode
  useEffect(() => {
    console.log(`Building items: mode=${reviewMode}, sources=${sources.length}, pages=${pages.length}`)
    if (reviewMode === 'domains') {
      setItems(sources.map(sourceToSwipeItem))
    } else {
      setItems(pages.map(pageToSwipeItem))
    }
    setCurrentIndex(0)
  }, [reviewMode, sources, pages, sourceToSwipeItem, pageToSwipeItem])

  const currentItem = items[currentIndex]
  const currentSource = sources[currentIndex] // Legacy - for compatibility

  // * Get total count of available items for checking
  const fetchTotalAvailableSources = async () => {
    if (!projectId) return

    try {
      if (reviewMode === 'domains') {
        // * Use backend statistics endpoint for accurate counts
        // * This avoids estimation errors from sampling
        const stats = await api.getProjectStatistics(Number(projectId))

        // * pending_count = domains waiting for manual review (swipe mode only uses PENDING)
        // * PRE_APPROVED domains are auto-approved from VDL and don't need manual review
        setTotalAvailableSources(stats.pending_count ?? 0)
      } else {
        // * Get pages statistics
        const stats = await api.getPagesStatistics(Number(projectId))
        setTotalAvailablePages(stats.available_pages)
      }
    } catch (error) {
      console.error('Fetch total available items error:', error)
      // * On error, don't set fallback - let it show "..." until data loads
    }
  }

  const loadSources = async () => {
    if (!projectId) return

    setLoading(true)
    try {
      console.log('Loading items for project:', projectId, 'operator:', operator?.id, 'mode:', reviewMode)

      if (reviewMode === 'domains') {
        // * Get locked sources for current operator
        const response = await api.getMyLockedSources(Number(projectId))
        console.log('Locked sources response:', response)

        // * Filter to only sources locked by current operator and domain type
        const pendingSources = response.sources.filter(
          (s: Source) => s.locked_by_operator_id === operator?.id &&
                         s.source_type === 'domain' &&
                         !skippedItemIds.has(`source_${s.id}`)
        )

        console.log('Pending sources after filter:', pendingSources.length)

        setSources(pendingSources)
        setPages([])
        setCurrentIndex(0)
      } else {
        // * Get locked pages for current operator
        const response = await api.getMyLockedPages(Number(projectId))
        console.log('Locked pages response:', response)

        // * Filter to only pages locked by current operator AND from current project
        const pendingPages = response.pages.filter(
          (p: ParsedPage) => p.locked_by_operator_id === operator?.id &&
                            !skippedItemIds.has(`page_${p.id}`)
        )

        console.log('Pending pages after filter:', pendingPages.length)

        setPages(pendingPages)
        setSources([])
        setCurrentIndex(0)
      }

      // * Reset stats
      const stats = {
        approved: 0,
        rejected: 0,
        uncertain: 0,
        skipped: 0,
      }
      setSwipeStats(stats)

      // * Load total available count FIRST, before potentially getting next batch
      await fetchTotalAvailableSources()

      // * Check if we need to load more
      const currentList = reviewMode === 'domains' ? sources : pages
      if (currentList.length === 0) {
        // * Automatically load batch if no items available
        await loadNextBatch()
      }
    } catch (error) {
      console.error('Load items error:', error)
      message.error(`Ошибка загрузки: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`)
    } finally {
      setLoading(false)
    }
  }

  // * Load batch of items (with configurable count) in the background
  const loadNextBatch = useCallback(async (count: number = 10, silent: boolean = false) => {
    if (!projectId || loadingBatch) return

    setLoadingBatch(true)

    try {
      console.log(`Loading batch of ${count} ${reviewMode}...`)

      if (reviewMode === 'domains') {
        // * Get batch of sources
        const batchResponse = await api.getNextBatch(Number(projectId), count)

        if (batchResponse.count > 0) {
          console.log(`Got ${batchResponse.count} sources in batch`)

          // * Wait a bit for all sources to be locked
          await new Promise(resolve => setTimeout(resolve, 500))

          // * Reload all locked sources
          const newResponse = await api.getMyLockedSources(Number(projectId))
          const newSources = newResponse.sources.filter(
            (s: Source) => s.locked_by_operator_id === operator?.id &&
                          s.source_type === 'domain' &&
                          !skippedItemIds.has(`source_${s.id}`)
          )

          console.log(`Total locked sources after batch: ${newSources.length}`)

          if (newSources.length > 0) {
            setSources(newSources)
            // * Keep current index if sources were already present
            if (sources.length > 0 && currentIndex < newSources.length) {
              // * Don't change index - user continues from current position
            } else {
              // * Start from beginning if no sources were present
              setCurrentIndex(0)
            }
          }

          // * Release any skipped sources that got locked
          const skippedInBatch = newResponse.sources.filter(
            (s: Source) => s.locked_by_operator_id === operator?.id && skippedItemIds.has(`source_${s.id}`)
          )

          if (skippedInBatch.length > 0) {
            console.log(`Releasing ${skippedInBatch.length} skipped sources from batch`)
            await Promise.all(skippedInBatch.map((s: Source) => api.releaseSource(s.id)))
          }
        } else {
          if (!silent) {
            message.info('Нет доступных источников для проверки')
          }
        }
      } else {
        // * Get batch of pages
        const batchResponse = await api.getNextPagesBatch(Number(projectId), count)

        if (batchResponse.count > 0) {
          console.log(`Got ${batchResponse.count} pages in batch`)

          // * Wait a bit for all pages to be locked
          await new Promise(resolve => setTimeout(resolve, 500))

          // * Reload all locked pages
          const newResponse = await api.getMyLockedPages(Number(projectId))
          const newPages = newResponse.pages.filter(
            (p: ParsedPage) => p.locked_by_operator_id === operator?.id &&
                             !skippedItemIds.has(`page_${p.id}`)
          )

          console.log(`Total locked pages after batch: ${newPages.length}`)

          if (newPages.length > 0) {
            setPages(newPages)
            // * Keep current index if pages were already present
            if (pages.length > 0 && currentIndex < newPages.length) {
              // * Don't change index - user continues from current position
            } else {
              // * Start from beginning if no pages were present
              setCurrentIndex(0)
            }
          }

          // * Release any skipped pages that got locked (including from other projects)
          const skippedInBatch = newResponse.pages.filter(
            (p: ParsedPage) => p.locked_by_operator_id === operator?.id &&
                             skippedItemIds.has(`page_${p.id}`)
          )

          if (skippedInBatch.length > 0) {
            console.log(`Releasing ${skippedInBatch.length} skipped pages from batch`)
            await Promise.all(skippedInBatch.map((p: ParsedPage) => api.releasePage(p.id)))
          }
        } else {
          if (!silent) {
            message.info('Нет доступных страниц для проверки')
          }
        }
      }
    } catch (error: any) {
      console.error('Load batch error:', error)
      if (!silent) {
        const errorMessage = error?.response?.data?.detail || error?.message || 'Неизвестная ошибка'
        message.error(`Ошибка загрузки: ${errorMessage}`)
      }
    } finally {
      setLoadingBatch(false)
    }
  }, [projectId, loadingBatch, operator?.id, skippedItemIds, sources.length, pages.length, currentIndex, reviewMode])

  // * Get direct URL for item (without proxy)
  const getDirectUrl = useCallback((item: SwipeItem | Source | null) => {
    if (!item) return ''

    // * Check if it's a SwipeItem
    if ('itemType' in item) {
      const swipeItem = item as SwipeItem
      if (swipeItem.itemType === 'page') {
        // * Pages already have full URL
        return swipeItem.displayUrl
      } else {
        // * Domains need https:// prefix
        let url = swipeItem.displayUrl
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          url = `https://${url}`
        }
        return url
      }
    }

    // * Legacy: Source object
    const source = item as Source
    let url = source.source
    if (source.source_type === 'domain') {
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = `https://${url}`
      }
    }

    return url
  }, [])

  // * Check iframe embeddability
  const checkIframeEmbeddability = useCallback(async (url: string, isForCurrentSource: boolean = false): Promise<boolean> => {
    // * Check cache first
    if (iframeCheckCache[url] !== undefined) {
      const cached = iframeCheckCache[url]
      if (!cached.embeddable && isForCurrentSource) {
        // * Automatically enable proxy for current source
        console.log('Site blocks embedding (cached), automatically enabling proxy:', url, '- Reason:', cached.reason || 'Unknown')
        setForceProxy(true)
      }
      return cached.embeddable
    }
    
    // * Check with backend
    try {
      if (isForCurrentSource) {
        setCheckingIframe(true)
      }
      const result = await api.checkIframe(url)
      
      // * Cache result
      setIframeCheckCache((prev) => ({
        ...prev,
        [url]: {
          embeddable: result.embeddable,
          reason: result.reason,
        },
      }))
      
      if (!result.embeddable && isForCurrentSource) {
        // * Automatically enable proxy for current source
        console.log('Site blocks embedding, automatically enabling proxy:', url, '- Reason:', result.reason || 'Unknown')
        setForceProxy(true)
        return false
      }
      
      return result.embeddable
    } catch (error) {
      console.error('Error checking iframe embeddability:', error)
      // * On error, assume embeddable
      setIframeCheckCache((prev) => ({
        ...prev,
        [url]: {
          embeddable: true,
          reason: 'Check failed',
        },
      }))
      return true
    } finally {
      if (isForCurrentSource) {
        setCheckingIframe(false)
      }
    }
  }, [iframeCheckCache])

  const getIframeUrl = useCallback((item: SwipeItem | Source | null) => {
    if (!item) return ''

    // * Get URL based on item type
    let url: string
    if ('itemType' in item) {
      // SwipeItem
      url = item.displayUrl
    } else {
      // Legacy Source
      const source = item as Source
      if (source.source_type === 'phone') {
        return ''
      }
      url = source.source
    }

    // * Ensure URL has protocol
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = `https://${url}`
    }

    // * Use proxy if forceProxy is true or keyword highlighting is enabled
    if (forceProxy || (highlightKeywords && keywords.trim())) {
      const encodedUrl = encodeURIComponent(url)
      const encodedKeywords = keywords.trim() ? encodeURIComponent(keywords) : ''
      const proxyUrl = `${import.meta.env.VITE_API_URL || '/api'}/sources/proxy-site?url=${encodedUrl}`
      return encodedKeywords ? `${proxyUrl}&keywords=${encodedKeywords}` : proxyUrl
    }

    return url
  }, [highlightKeywords, keywords, forceProxy])
  
  const handleIframeLoad = () => {
    setIframeLoaded(true)
    setIframeError(false)
    
    // * Try to get title directly from iframe (works for same-origin or proxied content)
    // * Use a small delay to ensure document is fully loaded
    const sourceId = currentItemIdRef.current
    setTimeout(() => {
      // * Only update if this iframe is still for the current source
      if (iframeRef.current && currentItemIdRef.current === sourceId) {
        try {
          const iframe = iframeRef.current
          if (iframe.contentWindow && iframe.contentDocument) {
            const title = iframe.contentDocument.title
            if (title) {
              // * Only update if title actually changed
              setPageTitle((prevTitle) => {
                if (prevTitle === title) {
                  return prevTitle // * No change, don't update
                }
                return title
              })
            }
          }
        } catch (e) {
          // * Cross-origin restrictions - will get title via postMessage instead
        }
      }
    }, 100)
  }

  const handleIframeError = () => {
    setIframeLoaded(false)
    setIframeError(true)
  }
  
  // * Handle preloaded iframe load/error
  const handlePreloadedIframeLoad = useCallback((sourceId: number) => {
    setPreloadedIframes(prev => ({
      ...prev,
      [sourceId]: {
        ...prev[sourceId],
        loaded: true,
        error: false,
      }
    }))
  }, [])
  
  const handlePreloadedIframeError = useCallback((sourceId: number) => {
    setPreloadedIframes(prev => ({
      ...prev,
      [sourceId]: {
        ...prev[sourceId],
        loaded: false,
        error: true,
      }
    }))
  }, [])

  // * Swipe action for pages (ParsedURLs)
  const swipeActionForPages = async (
    action: 'approved' | 'rejected' | 'uncertain' | 'skipped',
    reason?: string
  ) => {
    const currentPage = pages[currentIndex]
    if (!currentPage || isProcessingSwipe) return

    setIsProcessingSwipe(true)

    try {
      if (action === 'skipped') {
        // * Release lock without changing status
        await api.releasePage(currentPage.id)

        // * Add to skipped items
        setSkippedItemIds((prev) => new Set([...prev, `page_${currentPage.id}`]))

        // * Reset iframe state
        setIframeLoaded(false)
        setIframeError(false)

        // * Update total count
        setSwipeCountSinceLastTotalUpdate((prev) => prev + 1)
        if (swipeCountSinceLastTotalUpdate >= 4) {
          await fetchTotalAvailableSources()
          setSwipeCountSinceLastTotalUpdate(0)
        }

        // * Remove from current list and reload
        const updatedPages = pages.filter((p) => p.id !== currentPage.id)
        setPages(updatedPages)

        if (updatedPages.length > 0) {
          setCurrentIndex(0)
          loadNextBatch(1, true)
        } else {
          await loadNextBatch(10)
        }

        setIsProcessingSwipe(false)
        return
      }

      // * Update review status
      const reviewStatus = action === 'approved' ? 'approved'
        : action === 'rejected' ? 'rejected'
        : 'uncertain'

      await api.updatePageReviewStatus(currentPage.id, {
        review_status: reviewStatus,
        rejected_reason: reason,
      })

      // * Release lock after updating status
      await api.releasePage(currentPage.id)

      // * Auto-start phone extraction for approved pages
      if (action === 'approved') {
        try {
          await api.startBatchPhoneExtraction({
            project_id: Number(projectId),
            parsed_url_ids: [currentPage.id],
            methods: ['quick', 'phone_parser'],
          })
          message.success('Страница одобрена, сбор телефонов запущен')
        } catch (extractError) {
          console.error('Phone extraction error:', extractError)
          message.warning('Страница одобрена, но не удалось запустить сбор телефонов')
        }
      }

      // * Add to history
      setSwipeHistory((prev) => [
        ...prev,
        {
          item: pageToSwipeItem(currentPage),
          action,
          timestamp: Date.now(),
        },
      ])

      // * Update stats
      setSwipeStats((prev) => ({
        ...prev,
        [action]: prev[action] + 1,
      }))

      // * Update total count
      setSwipeCountSinceLastTotalUpdate((prev) => prev + 1)
      if (swipeCountSinceLastTotalUpdate >= 4) {
        await fetchTotalAvailableSources()
        setSwipeCountSinceLastTotalUpdate(0)
      }

      // * Move to next page
      const updatedPages = pages.filter((p) => p.id !== currentPage.id)
      setPages(updatedPages)

      if (updatedPages.length > 0) {
        setCurrentIndex(0)
        setIframeLoaded(false)
        setIframeError(false)
        loadNextBatch(1, true)
      } else {
        setCurrentIndex(0)
        await loadNextBatch(10)
      }

    } catch (error) {
      console.error('Swipe action for pages error:', error)
      message.error('Ошибка при обработке страницы')
    } finally {
      setIsProcessingSwipe(false)
    }
  }

  const swipeAction = async (
    action: 'approved' | 'rejected' | 'uncertain' | 'skipped',
    reason?: string
  ) => {
    // * Handle based on review mode
    if (reviewMode === 'pages') {
      await swipeActionForPages(action, reason)
      return
    }

    // * Original domains logic
    if (!currentSource || isProcessingSwipe) return

    setIsProcessingSwipe(true)

    try {
      let newStatus: string

      if (action === 'approved') {
        newStatus = 'active'  // МЫ одобрили в AFI
      } else if (action === 'rejected') {
        newStatus = 'inactive'  // Отклонили
      } else if (action === 'uncertain') {
        // ! Сомневаюсь - возвращаем оригинальный статус (какой был до блокировки)
        // ! currentSource.status содержит оригинальный статус (не меняется при блокировке)
        newStatus = currentSource.status  // Возвращаем как было
      } else {
        // Skipped - don't change status, just release lock
        const skippedSourceId = currentSource.id

        // ! Освобождаем блокировку для skipped
        await api.releaseSource(skippedSourceId)

        // * Add to skipped items list to avoid showing it again
        setSkippedItemIds((prev) => new Set([...prev, `source_${skippedSourceId}`]))
        
        // * Reset iframe state since we're moving to a new source
        setIframeLoaded(false)
        setIframeError(false)
        
        // * Update total count only every 5 swipes to reduce API calls
        setSwipeCountSinceLastTotalUpdate((prev) => prev + 1)
        if (swipeCountSinceLastTotalUpdate >= 4) {
          await fetchTotalAvailableSources()
          setSwipeCountSinceLastTotalUpdate(0)
        }
        
        // * Remove skipped source from current list immediately
        const updatedSources = sources.filter((s) => s.id !== skippedSourceId)
        
        // * Create updated skipped items list
        const currentSkippedIds = new Set([...skippedItemIds, `source_${skippedSourceId}`])
        
        // * Reload sources to ensure we don't show the released source again
        try {
          // * Wait a bit for the release to propagate
          await new Promise(resolve => setTimeout(resolve, 200))
          
          const reloadResponse = await api.getMyLockedSources(Number(projectId))
          const reloadedSources = reloadResponse.sources.filter(
            (s: Source) => s.locked_by_operator_id === operator?.id &&
                          s.source_type === 'domain' &&
                          !currentSkippedIds.has(`source_${s.id}`)
          )
          
          if (reloadedSources.length > 0) {
            // * Show the first source (excluding the skipped one)
            setSources(reloadedSources)
            setCurrentIndex(0)
            
            // * Load 1 more source to maintain pool of PRELOAD_COUNT ahead
            loadNextBatch(1, true)
          } else {
            // * No sources left, try to load full batch (excluding skipped sources)
            setSources([])
            await loadNextBatch(10)
          }
        } catch (error) {
          console.error('Error reloading sources after skip:', error)
          // * Fallback: use updated sources list
          if (updatedSources.length > 0) {
            setSources(updatedSources)
            setCurrentIndex(0)
            
            // * Load 1 more source to maintain pool
            loadNextBatch(1, true)
          } else {
            setSources(updatedSources)
            await loadNextBatch(10)
          }
        }
        
        setIsProcessingSwipe(false)
        return
      }

      // * Update source status and will_work
      // ! will_work зависит от действия:
      // ! approved → true (одобрили, работает)
      // ! rejected → false (отклонили, не работает)
      // ! uncertain → оставляем как было (не решили)
      const updateData: { status: string; will_work?: boolean; rejected_reason?: string } = {
        status: newStatus,
      }
      
      if (action === 'approved') {
        updateData.will_work = true
      } else if (action === 'rejected') {
        updateData.will_work = false
        // * Save rejection reason if provided
        if (reason) {
          updateData.rejected_reason = reason
        }
      }
      // * Для uncertain НЕ меняем will_work - оставляем как было
      
      // * Retry logic for transient errors (e.g., token expiration, network issues)
      const MAX_RETRIES = 2
      let retryCount = 0
      let updateSuccess = false
      let releaseSuccess = false
      
      // * Update source with retry
      while (retryCount < MAX_RETRIES && !updateSuccess) {
        try {
          await api.updateSource(currentSource.id, updateData)
          updateSuccess = true
        } catch (err: any) {
          retryCount++
          if (retryCount >= MAX_RETRIES) {
            throw new Error(`Failed to update source after ${MAX_RETRIES} attempts: ${err.message || 'Unknown error'}`)
          }
          // * Wait before retry (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 500 * retryCount))
        }
      }
      
      // * Release source with retry
      retryCount = 0
      while (retryCount < MAX_RETRIES && !releaseSuccess) {
        try {
          // ! Освобождаем блокировку для ВСЕХ действий
          // ! Если источник обработан (active/inactive) → больше не нужен на проверку
          // ! Если uncertain → освобождаем для других операторов
          await api.releaseSource(currentSource.id)
          releaseSuccess = true
        } catch (err: any) {
          retryCount++
          if (retryCount >= MAX_RETRIES) {
            // * Log error but don't fail the entire operation if release fails
            // * Source will auto-release after timeout anyway
            console.error(`Failed to release source after ${MAX_RETRIES} attempts:`, err)
            message.warning('Источник обновлен, но блокировка может не освободиться сразу')
            break
          }
          // * Wait before retry
          await new Promise(resolve => setTimeout(resolve, 500 * retryCount))
        }
      }

      // * Add to history
      setSwipeHistory((prev) => [
        ...prev,
        {
          item: sourceToSwipeItem(currentSource),
          action,
          timestamp: Date.now(),
        },
      ])

      // * Update stats
      setSwipeStats((prev) => ({
        ...prev,
        [action]: prev[action] + 1,
      }))

      // * Auto-start phone extraction for approved domains
      if (action === 'approved') {
        try {
          const result = await api.extractPhonesFromSource({
            source_id: currentSource.id,
            methods: ['quick', 'phone_parser'],
          })
          message.success(`Источник одобрен, сбор телефонов запущен (${result.parsed_urls_count} страниц)`)
        } catch (extractError: any) {
          console.error('Phone extraction error:', extractError)
          const detail = extractError.response?.data?.detail || ''
          if (detail.includes('already completed') || detail.includes('already running')) {
            message.success('Источник одобрен (телефоны уже собираются/собраны)')
          } else if (detail.includes('No pages pending') || detail.includes('No approved pages')) {
            message.success('Источник одобрен (нет страниц для сбора телефонов)')
          } else {
            message.warning('Источник одобрен, но не удалось запустить сбор телефонов')
          }
        }
      } else if (action === 'rejected') {
        message.success('Источник отклонен')
      } else if (action === 'uncertain') {
        message.info('Источник возвращен в режим проверки для других операторов')
      }

      // * Remove from current sources list and move to next
      const updatedSources = sources.filter((s) => s.id !== currentSource.id)
      setSources(updatedSources)
      
      // * Update total count only every 5 swipes to reduce API calls
      setSwipeCountSinceLastTotalUpdate((prev) => prev + 1)
      if (swipeCountSinceLastTotalUpdate >= 4) {
        await fetchTotalAvailableSources()
        setSwipeCountSinceLastTotalUpdate(0)
      }
      
      // * If no more sources, load full batch
      if (updatedSources.length === 0) {
        loadNextBatch(10)
      } else {
        // * Load 1 more source to maintain pool of PRELOAD_COUNT ahead
        loadNextBatch(1, true)
        
        // * Stay at current index (which now points to next source)
        setIframeLoaded(false)
        setIframeError(false)
      }
    } catch (error: any) {
      console.error('Swipe action error:', error)
      
      // * Provide more specific error messages
      let errorMessage = 'Ошибка обновления статуса'
      
      if (error.message?.includes('Failed to update source')) {
        errorMessage = 'Не удалось обновить источник. Попробуйте еще раз.'
      } else if (error.response?.status === 401) {
        errorMessage = 'Сессия истекла. Пожалуйста, войдите снова.'
      } else if (error.response?.status === 404) {
        errorMessage = 'Источник не найден'
      } else if (error.response?.status === 403) {
        errorMessage = 'Недостаточно прав для выполнения операции'
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Превышено время ожидания. Проверьте соединение.'
      } else if (error.message?.includes('Network Error')) {
        errorMessage = 'Ошибка сети. Проверьте соединение.'
      }
      
      message.error(errorMessage)
    } finally {
      setIsProcessingSwipe(false)
    }
  }

  const handleApprove = () => {
    swipeAction('approved')
  }

  const handleReject = () => {
    if (!currentItem) return
    setPendingRejection(currentItem)
    setCustomReasonMode(false)
    setCustomReason('')
    setRejectModalVisible(true)
  }

  const handleUncertain = () => {
    swipeAction('uncertain')
  }

  const undoLastSwipe = async () => {
    if (swipeHistory.length === 0) return

    const lastAction = swipeHistory[swipeHistory.length - 1]
    const lastItem = lastAction.item

    try {
      // * Revert status on server (except for 'skipped' which didn't change status)
      if (lastAction.action !== 'skipped') {
        if (lastItem.itemType === 'source' && lastItem.originalSource) {
          const originalStatus = lastItem.originalSource.status
          await api.updateSource(lastItem.id, {
            status: originalStatus,
            will_work: lastItem.originalSource.will_work,
          })
        } else if (lastItem.itemType === 'page') {
          // * For pages, reset review_status to null
          await api.updatePageReviewStatus(lastItem.id, {
            review_status: 'uncertain', // Reset to uncertain
          })
        }
      }

      // * Remove from skipped items if it was skipped
      if (lastAction.action === 'skipped') {
        setSkippedItemIds((prev) => {
          const newSet = new Set(prev)
          const key = `${lastItem.itemType}_${lastItem.id}`
          newSet.delete(key)
          return newSet
        })
      }

      // * Remove from history
      setSwipeHistory((prev) => prev.slice(0, -1))

      // * Update stats
      setSwipeStats((prev) => ({
        ...prev,
        [lastAction.action]: Math.max(0, prev[lastAction.action] - 1),
      }))

      // * Add item back to the list and show it
      if (lastItem.itemType === 'source' && lastItem.originalSource) {
        setSources((prev) => [lastItem.originalSource!, ...prev])
      } else if (lastItem.itemType === 'page' && lastItem.originalPage) {
        setPages((prev) => [lastItem.originalPage!, ...prev])
      }
      setCurrentIndex(0)
      setIframeLoaded(false)
      setIframeError(false)

      // * Update total count (always update on undo for accuracy)
      await fetchTotalAvailableSources()
      setSwipeCountSinceLastTotalUpdate(0)

      message.success('Действие отменено')
    } catch (error) {
      console.error('Undo swipe error:', error)
      message.error('Ошибка отмены действия')
    }
  }

  // * Handle exit from work mode
  const handleExitWorkMode = async () => {
    if (projectId && operator?.id) {
      try {
        // * Release both sources and pages
        await Promise.all([
          api.releaseAllSources(Number(projectId)),
          api.releaseAllPages(Number(projectId)),
        ])
      } catch (error) {
        console.error('Error releasing items on exit:', error)
      }
    }
    // * Navigate to project sources page instead of browser history back
    navigate(`/projects/${projectId}/sources`)
  }

  // * Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // * Don't process shortcuts if processing a swipe
      if (isProcessingSwipe) return
      
      // * Don't process shortcuts if modal is open
      if (rejectModalVisible || statusModalVisible) return
      
      // * Don't process shortcuts if user is typing in an input field
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.isContentEditable
      ) {
        return
      }

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault()
          handleReject()
          break
        case 'ArrowRight':
          e.preventDefault()
          handleApprove()
          break
        case 'ArrowUp':
          e.preventDefault()
          handleUncertain()
          break
        case 'ArrowDown':
          e.preventDefault()
          undoLastSwipe()
          break
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [currentItem, isProcessingSwipe, rejectModalVisible, statusModalVisible])

  // * Save isolated mode setting to localStorage
  useEffect(() => {
    localStorage.setItem('swipe_isolated_mode', String(isolatedMode))
  }, [isolatedMode])

  // * Clear browser storage (cookies, localStorage, sessionStorage for this domain)
  const handleClearStorage = useCallback(() => {
    // * Clear localStorage (except our settings)
    const isolatedModeSetting = localStorage.getItem('swipe_isolated_mode')
    localStorage.clear()
    if (isolatedModeSetting !== null) {
      localStorage.setItem('swipe_isolated_mode', isolatedModeSetting)
    }
    
    // * Clear sessionStorage
    sessionStorage.clear()
    
    // * Clear cookies for current domain
    document.cookie.split(';').forEach(cookie => {
      const eqPos = cookie.indexOf('=')
      const name = eqPos > -1 ? cookie.slice(0, eqPos).trim() : cookie.trim()
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
    })
    
    // * Clear iframe cache
    setIframeCheckCache({})
    setPreloadedIframes({})
    
    message.success('Хранилище браузера очищено. Cookies от iframe будут удалены при перезагрузке страницы.')
    
    // * Reload the iframe by resetting state
    if (currentItem) {
      setIframeLoaded(false)
      setIframeError(false)
      setForceProxy(false)
    }
  }, [currentItem])

  // * Load VDL TZ from external API
  const loadVdlTz = useCallback(async () => {
    if (!projectId) return

    try {
      console.log('Loading VDL TZ for project:', projectId)
      
      // * Fetch vdl_tz from backend API (avoids CORS issues)
      const response = await api.getVdlTz(Number(projectId))
      console.log('VDL TZ API response:', response)
      
      const tz = response?.vdl_tz || null
      console.log('Extracted vdl_tz:', tz, 'Type:', typeof tz)
      
      if (tz && typeof tz === 'string' && tz.trim().length > 0) {
        const trimmedTz = tz.trim()
        console.log('Setting VDL TZ:', trimmedTz)
        setVdlTz(trimmedTz)
      } else {
        console.warn('vdl_tz is empty or invalid:', tz)
        setVdlTz(null)
      }
    } catch (error: any) {
      console.error('Error loading vdl_tz:', {
        error,
        message: error?.response?.data?.detail || error?.message,
        status: error?.response?.status,
      })
      setVdlTz(null)
    }
  }, [projectId])

  useEffect(() => {
    loadSources()
    loadVdlTz()
  }, [projectId, loadVdlTz, reviewMode])

  // * Fetch project data for status popup (parallel with source loading)
  useEffect(() => {
    if (!projectId) return
    let cancelled = false
    const fetchProjectInfo = async () => {
      try {
        const data = await api.getProject(Number(projectId))
        if (!cancelled) {
          setProjectInfo(data)
          setStatusModalVisible(true)
        }
      } catch (error) {
        console.error('Error loading project for status popup:', error)
      }
    }
    fetchProjectInfo()
    return () => { cancelled = true }
  }, [projectId])

  // * NOTE: Removed automatic batch loading based on threshold
  // * Now we load 1 source after each swipe action to maintain constant pool of PRELOAD_COUNT ahead

  // * Cleanup: Release all locked items when component unmounts
  useEffect(() => {
    return () => {
      // * Release all sources and pages locked by this operator when leaving the page
      if (projectId && operator?.id) {
        Promise.all([
          api.releaseAllSources(Number(projectId)),
          api.releaseAllPages(Number(projectId)),
        ]).catch((error) => {
          console.error('Error releasing items on unmount:', error)
        })
      }
    }
  }, [projectId, operator?.id])

  // * Handle page unload (browser close/refresh)
  useEffect(() => {
    const handleBeforeUnload = () => {
      // * Release all sources when page is about to unload
      // * Note: beforeunload handler has limitations with auth headers
      // * The cleanup function in useEffect will handle most cases
      if (projectId && operator?.id) {
        // * Attempt to release sources (may not complete if page closes)
        const url = new URL(`${import.meta.env.VITE_API_URL || '/api'}/sources/release-all`, window.location.origin)
        if (projectId) {
          url.searchParams.set('project_id', String(projectId))
        }
        // * Note: sendBeacon doesn't support custom headers, so auth won't work
        // * This is best-effort, main cleanup happens in useEffect cleanup
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [projectId, operator?.id])

  // * Preload next sources (only for domains mode)
  useEffect(() => {
    if (reviewMode !== 'domains' || !currentSource || sources.length === 0) return
    
    // * Preload next PRELOAD_COUNT sources
    const sourcesToPreload = sources.slice(currentIndex + 1, currentIndex + 1 + PRELOAD_COUNT)
    
    const preloadSources = async () => {
      for (const source of sourcesToPreload) {
        // * Skip if already preloaded
        if (preloadedIframes[source.id]) continue
        
        // * Skip non-domain sources
        if (source.source_type !== 'domain') continue
        
        const directUrl = getDirectUrl(source)
        if (!directUrl) continue
        
        // * Check iframe embeddability first
        const isEmbeddable = await checkIframeEmbeddability(directUrl)
        
        const url = getIframeUrl(source)
        
        setPreloadedIframes((prev) => ({
          ...prev,
          [source.id]: {
            loaded: false,
            error: false,
            blocked: !isEmbeddable,
            blockReason: iframeCheckCache[directUrl]?.reason,
            url,
            title: prev[source.id]?.title, // * Preserve title if already received
          }
        }))
      }
    }
    
    preloadSources()
  }, [currentIndex, sources.length])
  
  useEffect(() => {
    if (!currentItem) return

    // * Update ref to track current item ID
    currentItemIdRef.current = currentItem.id

    // * Reset forceProxy when item changes
    setForceProxy(false)

    // * Use preloaded data if available
    const preloaded = preloadedIframes[currentItem.id]
    if (preloaded) {
      // * Apply preloaded state immediately (proxy will be enabled by separate useEffect if needed)
      setIframeLoaded(preloaded.loaded)
      setIframeError(preloaded.error)
      setCheckingIframe(false)

      // * Use preloaded title if available, otherwise reset
      setPageTitle(preloaded.title || null)
    } else {
      // * Reset iframe state when item changes and no preloaded data
      setIframeLoaded(false)
      setIframeError(false)
      setPageTitle(null)

      // * Check iframe embeddability when item changes
      const directUrl = getDirectUrl(currentItem)
      if (directUrl) {
        checkIframeEmbeddability(directUrl, true) // * true = is for current item
      }
    }
  }, [currentItem?.id, currentItem?.itemType])
  
  // * Update iframe state when preloaded iframe finishes loading
  useEffect(() => {
    if (!currentItem) return

    const preloaded = preloadedIframes[currentItem.id]
    if (preloaded && preloaded.loaded && !iframeLoaded && !forceProxy) {
      setIframeLoaded(true)
    }
  }, [preloadedIframes, currentItem?.id, iframeLoaded, forceProxy])

  // * Auto-enable proxy when preloaded iframe is blocked
  useEffect(() => {
    if (!currentItem || forceProxy) return

    const preloaded = preloadedIframes[currentItem.id]
    if (preloaded && preloaded.blocked) {
      console.log('Preloaded iframe detected as blocked, automatically enabling proxy for:', currentItem.displayUrl)
      setForceProxy(true)
    }
  }, [preloadedIframes, currentItem?.id, forceProxy])
  
  // * Update page title when preloaded title becomes available for current item
  useEffect(() => {
    if (!currentItem) return

    const preloaded = preloadedIframes[currentItem.id]
    if (preloaded?.title && !pageTitle) {
      setPageTitle(preloaded.title)
    }
  }, [preloadedIframes, currentItem?.id, pageTitle])
  
  // * Listen for postMessage from iframe to get page title
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // * Accept messages from any origin (iframe content)
      if (event.data && event.data.type === 'iframe-title' && event.data.title && event.data.url) {
        const messageUrl = event.data.url
        const messageTitle = event.data.title

        // * Find which item this URL belongs to (could be current or preloaded)
        const matchingItem = items.find((item) => {
          const itemUrl = getDirectUrl(item)
          return itemUrl === messageUrl
        })

        if (matchingItem) {
          // * Save title to preloaded cache for this item
          setPreloadedIframes((prev) => {
            const existing = prev[matchingItem.id]
            // * If no existing record, create minimal one with title
            if (!existing) {
              return {
                ...prev,
                [matchingItem.id]: {
                  loaded: false,
                  error: false,
                  blocked: false,
                  url: getDirectUrl(matchingItem),
                  title: messageTitle,
                }
              }
            }
            // * Update existing record with title
            return {
              ...prev,
              [matchingItem.id]: {
                ...existing,
                title: messageTitle,
              }
            }
          })

          // * If this is the current item, update displayed title
          if (currentItem && matchingItem.id === currentItem.id) {
            setPageTitle((prevTitle) => {
              if (prevTitle === messageTitle) {
                return prevTitle // * No change, don't update
              }
              return messageTitle
            })
          }
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [items, currentItem, getDirectUrl])

  // * Status modal rendered outside early returns so it's always visible
  const statusModal = (
    <Modal
      title={null}
      open={statusModalVisible && projectInfo !== null}
      onCancel={() => setStatusModalVisible(false)}
      footer={[
        <Button
          key="ok"
          type="primary"
          size="large"
          onClick={() => setStatusModalVisible(false)}
          style={{ width: '100%' }}
        >
          Понятно
        </Button>,
      ]}
      width={440}
      centered
    >
      {projectInfo && (() => {
        const isPaused = projectInfo.status === 'pause'
        const isActive = projectInfo.status === 'active' || projectInfo.status === 'test_active'
        const phonesOff = !projectInfo.parse_phones
        const domainsOff = !projectInfo.parse_domains
        const hasProblems = isPaused || phonesOff || domainsOff

        const statusLabels: Record<string, string> = {
          active: 'Активен',
          test_active: 'Тест активен',
          pause: 'Пауза',
          stop: 'Остановлен',
          test_stop: 'Тест остановлен',
        }

        return (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div style={{ textAlign: 'center', paddingBottom: 4 }}>
              <Text strong style={{ fontSize: 16 }}>{projectInfo.project_name}</Text>
              <br />
              <a
                href={`https://victory-crm.ru/projects/${projectInfo.crm_id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 12 }}
              >
                <LinkOutlined /> Открыть в VDL
              </a>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              padding: '12px 16px',
              background: '#fafafa',
              borderRadius: 8,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text>Статус проекта</Text>
                <Tag
                  color={isActive ? 'success' : isPaused ? 'error' : 'default'}
                  icon={isActive ? <CheckCircleOutlined /> : isPaused ? <PauseCircleOutlined /> : <ExclamationCircleOutlined />}
                  style={{ margin: 0 }}
                >
                  {statusLabels[projectInfo.status] || projectInfo.status}
                </Tag>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text><PhoneOutlined /> Парсинг номеров</Text>
                <Tag
                  color={projectInfo.parse_phones ? 'success' : 'warning'}
                  icon={projectInfo.parse_phones ? <CheckCircleOutlined /> : <WarningOutlined />}
                  style={{ margin: 0 }}
                >
                  {projectInfo.parse_phones ? 'Включен' : 'Выключен'}
                </Tag>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text><GlobalOutlined /> Сбор исходов</Text>
                <Tag
                  color={projectInfo.parse_domains ? 'success' : 'warning'}
                  icon={projectInfo.parse_domains ? <CheckCircleOutlined /> : <WarningOutlined />}
                  style={{ margin: 0 }}
                >
                  {projectInfo.parse_domains ? 'Включен' : 'Выключен'}
                </Tag>
              </div>
            </div>

            {isPaused && (
              <Alert
                type="error"
                showIcon
                message="Проект на паузе"
                description="Тоглы парсинга в VDL автоматически отключены. Источники, одобренные в свайпере сейчас, не будут парситься. После возобновления проекта необходимо вручную включить парсинг для каждого источника в VDL."
              />
            )}

            {!isPaused && hasProblems && (
              <Alert
                type="warning"
                showIcon
                message="Парсинг частично отключен"
                description={`${phonesOff ? 'Парсинг номеров выключен. ' : ''}${domainsOff ? 'Сбор исходов выключен. ' : ''}Включите тоглы в VDL перед одобрением источников.`}
              />
            )}

            {!hasProblems && (
              <Alert
                type="success"
                showIcon
                message="Все тоглы включены"
                description="Парсинг номеров и сбор исходов активны. Одобренные источники будут обработаны автоматически."
              />
            )}
          </Space>
        )
      })()}
    </Modal>
  )

  if (loading && sources.length === 0) {
    return (
      <>
        {statusModal}
        <div style={{ padding: '50px', textAlign: 'center' }}>
          <Text>Загрузка источников...</Text>
        </div>
      </>
    )
  }

  if (items.length === 0 && !loading) {
    return (
      <>
        {statusModal}
        <div style={{ padding: '50px', textAlign: 'center' }}>
          <Title level={2}>Нет {reviewMode === 'domains' ? 'источников' : 'страниц'} для проверки</Title>
          <Space direction="vertical" size="large" style={{ marginTop: 24 }}>
            {/* Mode selector even when empty */}
            <Segmented
              value={reviewMode}
              onChange={(value) => setReviewMode(value as ReviewMode)}
              options={[
                { label: '🌐 Домены', value: 'domains' },
                { label: '📄 Страницы', value: 'pages' },
              ]}
            />
            <Text type="secondary">
              Получение {reviewMode === 'domains' ? 'нового источника' : 'новой страницы'}...
            </Text>
            <Space>
              {!embedded && <Button onClick={handleExitWorkMode}>Вернуться назад</Button>}
              <Button type="primary" onClick={loadSources}>
                Обновить
              </Button>
            </Space>
          </Space>
        </div>
      </>
    )
  }

  const iframeUrl = getIframeUrl(currentItem)

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#f5f5f5' }}>
      {/* * Hidden preload iframes */}
      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}>
        {sources.slice(currentIndex + 1, currentIndex + 1 + PRELOAD_COUNT).map((source) => {
          const preloadData = preloadedIframes[source.id]
          if (!preloadData || source.source_type !== 'domain') return null
          
          // * Use proxy URL if blocked
          let iframeUrl = preloadData.url
          if (preloadData.blocked) {
            const directUrl = getDirectUrl(source)
            const encodedUrl = encodeURIComponent(directUrl || '')
            iframeUrl = `${import.meta.env.VITE_API_URL || '/api'}/sources/proxy-site?url=${encodedUrl}`
          }
          
          return (
            <iframe
              key={`${source.id}-${preloadData.blocked ? 'proxy' : 'direct'}-${isolatedMode ? 'isolated' : 'normal'}`}
              src={iframeUrl}
              style={{ width: '1px', height: '1px' }}
              onLoad={() => handlePreloadedIframeLoad(source.id)}
              onError={() => handlePreloadedIframeError(source.id)}
              sandbox={isolatedMode ? 'allow-scripts' : 'allow-scripts allow-same-origin'}
            />
          )
        })}
      </div>
      
      {/* * Compact Header */}
      <Card
        style={{
          borderRadius: 0,
          borderLeft: 0,
          borderRight: 0,
          borderTop: 0,
          marginBottom: 0,
          padding: '8px 16px',
        }}
        bodyStyle={{ padding: '8px 16px' }}
      >
        <Row align="middle" gutter={16} wrap={false}>
          {/* * Back button (hidden when embedded — no "exit to project" in iframe) */}
          {!embedded && (
            <Col flex="none">
              <Button
                icon={<LeftOutlined />}
                onClick={handleExitWorkMode}
                size="small"
                style={{ minWidth: 'auto' }}
              />
            </Col>
          )}

          {/* * Mode selector */}
          <Col flex="none">
            <Segmented
              value={reviewMode}
              onChange={(value) => setReviewMode(value as ReviewMode)}
              options={[
                { label: '🌐 Домены', value: 'domains' },
                { label: '📄 Страницы', value: 'pages' },
              ]}
              size="small"
            />
          </Col>

          <Col flex="none">
            <div style={{ width: 1, height: 20, background: '#e8e8e8' }} />
          </Col>

          {/* * Main stats - compact horizontal layout */}
          <Col flex="auto">
            <Row gutter={[12, 0]} align="middle" wrap={false}>
              {/* * Stats */}
              <Col flex="none">
                <Space size={16} wrap={false}>
                  <Text style={{ fontSize: 12 }}>
                    <span style={{ color: '#52c41a' }}>✅ Одобрено</span>
                    <span style={{ fontWeight: 'bold', marginLeft: 4 }}>{swipeStats.approved}</span>
                  </Text>
                  <Text style={{ fontSize: 12 }}>
                    <span style={{ color: '#ff4d4f' }}>❌ Отклонено</span>
                    <span style={{ fontWeight: 'bold', marginLeft: 4 }}>{swipeStats.rejected}</span>
                  </Text>
                  <Text style={{ fontSize: 12 }}>
                    <span style={{ color: '#faad14' }}>🤔 Сомневаюсь</span>
                    <span style={{ fontWeight: 'bold', marginLeft: 4 }}>{swipeStats.uncertain}</span>
                  </Text>
                  <Text style={{ fontSize: 12 }}>
                    <span>Осталось {reviewMode === 'domains' ? 'доменов' : 'страниц'}</span>
                    <span style={{ fontWeight: 'bold', marginLeft: 4 }}>
                      {reviewMode === 'domains'
                        ? (totalAvailableSources > 0 ? totalAvailableSources : '...')
                        : (totalAvailablePages > 0 ? totalAvailablePages : '...')}
                    </span>
                    {loadingBatch && (
                      <span style={{ marginLeft: 4, color: '#1890ff' }}>⏳</span>
                    )}
                  </Text>
                </Space>
              </Col>

              <Col flex="none">
                <div style={{ width: 1, height: 20, background: '#e8e8e8' }} />
              </Col>

              {/* * Keyword highlighting */}
              <Col flex="none">
                <Space size={8} wrap={false}>
                  <Text style={{ fontSize: 12 }}>Подсветка ключевых слов:</Text>
                  <Switch 
                    checked={highlightKeywords} 
                    onChange={setHighlightKeywords}
                    size="small"
                  />
                  <Input
                    placeholder="слова через запятую"
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                    disabled={!highlightKeywords}
                    size="small"
                    style={{ width: 200 }}
                  />
                </Space>
              </Col>

              <Col flex="none">
                <div style={{ width: 1, height: 20, background: '#e8e8e8' }} />
              </Col>

              {/* * Isolated mode - prevents iframe from saving cookies */}
              <Col flex="none">
                <Space size={8} wrap={false}>
                  <Text style={{ fontSize: 12 }} title="Без cookies - iframe не сохраняет cookies и данные сайтов">
                    Изолированный режим:
                  </Text>
                  <Switch 
                    checked={isolatedMode} 
                    onChange={setIsolatedMode}
                    size="small"
                  />
                </Space>
              </Col>
            </Row>
          </Col>

          {/* * Action buttons */}
          <Col flex="none">
            <Space size={8}>
              <Button 
                icon={<ClearOutlined />}
                onClick={handleClearStorage}
                size="small"
                title="Очистить cookies и кэш браузера"
              >
                Очистить cookies
              </Button>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={loadSources}
                size="small"
              >
                Обновить
              </Button>
              <Button
                icon={<UndoOutlined />}
                onClick={undoLastSwipe}
                disabled={swipeHistory.length === 0}
                size="small"
              >
                Отменить
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* * Main swipe area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {currentItem && (
          <Card
            style={{
              flex: 1,
              margin: 16,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
            bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 0 }}
          >
            {/* * Source info - compact */}
            <div style={{ padding: 12, borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flexShrink: 0 }}>
                  <Text strong style={{ fontSize: 13 }}>
                    {currentItem.itemType === 'page' ? 'Страница: ' : 'Источник: '}
                  </Text>
                  {currentItem.itemType === 'source' && currentItem.originalSource?.source_type === 'phone' ? (
                    <Text copyable style={{ fontSize: 13 }}>{currentItem.displayUrl}</Text>
                  ) : (
                    <Link
                      href={getDirectUrl(currentItem)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 13 }}
                    >
                      {currentItem.displayUrl}
                    </Link>
                  )}
                </div>
                {/* * AI Score badge */}
                {currentItem.itemType === 'source' && currentItem.originalSource?.ai_score != null && (
                  <>
                    <Text style={{ fontSize: 13, color: '#8c8c8c' }}>|</Text>
                    <div style={{ flexShrink: 0 }}>
                      <Tag
                        color={
                          currentItem.originalSource.ai_score >= 70
                            ? 'green'
                            : currentItem.originalSource.ai_score >= 40
                            ? 'orange'
                            : 'red'
                        }
                        style={{ margin: 0, fontWeight: 600 }}
                      >
                        AI: {currentItem.originalSource.ai_score}
                      </Tag>
                    </div>
                  </>
                )}
                {/* * AI Comment */}
                {currentItem.itemType === 'source' && currentItem.originalSource?.ai_comment && (
                  <>
                    <Text style={{ fontSize: 13, color: '#8c8c8c' }}>|</Text>
                    <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                      <Text
                        style={{ fontSize: 13, color: '#595959' }}
                        ellipsis={{ tooltip: { title: currentItem.originalSource.ai_comment, overlayStyle: { maxWidth: 500 } } }}
                      >
                        {currentItem.originalSource.ai_comment}
                      </Text>
                    </div>
                  </>
                )}
                {/* * Pending phones from Auto Load API */}
                {currentItem.itemType === 'source' && currentItem.originalSource?.pending_phones && currentItem.originalSource.pending_phones.length > 0 && (
                  <>
                    <Text style={{ fontSize: 13, color: '#8c8c8c' }}>|</Text>
                    <div style={{ flexShrink: 0 }}>
                      <Tag color="orange" style={{ margin: 0 }}>
                        API: {currentItem.originalSource.pending_phones.join(', ')}
                      </Tag>
                    </div>
                  </>
                )}
                {pageTitle && (
                  <>
                    <Text style={{ fontSize: 13, color: '#8c8c8c' }}>|</Text>
                    <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                      <Text strong style={{ fontSize: 13 }}>Заголовок: </Text>
                      <Text
                        style={{ fontSize: 13 }}
                        ellipsis={{ tooltip: pageTitle }}
                        title={pageTitle}
                      >
                        {pageTitle}
                      </Text>
                    </div>
                  </>
                )}
                {vdlTz && (
                  <>
                    <Text style={{ fontSize: 13, color: '#8c8c8c' }}>|</Text>
                    <div style={{ flexShrink: 0 }}>
                      <Text strong style={{ fontSize: 13 }}>ТЗ: </Text>
                      <Text style={{ fontSize: 13 }}>{vdlTz}</Text>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* * Proxy warning banner - outside iframe */}
            {forceProxy && (
              <div
                style={{
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%)',
                  padding: '8px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  borderBottom: '1px solid #ff4757',
                }}
              >
                <Text style={{ color: 'white', fontSize: '13px' }}>
                  🔄 Загружено через прокси (сайт блокирует встраивание)
                </Text>
                <Button
                  size="small"
                  type="link"
                  href={getDirectUrl(currentItem)}
                  target="_blank"
                  style={{ color: 'white', padding: '0 8px' }}
                >
                  Открыть напрямую →
                </Button>
              </div>
            )}

            {/* * Iframe or placeholder */}
            <div style={{ flex: 1, position: 'relative', background: '#f5f5f5' }}>
              {currentItem.itemType === 'source' && currentItem.originalSource?.source_type === 'phone' ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    flexDirection: 'column',
                  }}
                >
                  <Text style={{ fontSize: 24 }}>📞</Text>
                  <Text style={{ fontSize: 18, marginTop: 16 }}>
                    Телефон: {currentItem.displayUrl}
                  </Text>
                  <Text type="secondary" style={{ marginTop: 8 }}>
                    Предпросмотр недоступен для телефонных номеров
                  </Text>
                </div>
              ) : iframeUrl ? (
                <>
                  {/* * Show loading states */}
                  {checkingIframe && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                        zIndex: 10,
                      }}
                    >
                      <Text>Проверка доступности сайта...</Text>
                    </div>
                  )}
                  {!iframeLoaded && !preloadedIframes[currentItem.id]?.loaded && !iframeError && !checkingIframe && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                      }}
                    >
                      <Text>{forceProxy ? 'Загрузка через прокси...' : 'Загрузка сайта...'}</Text>
                    </div>
                  )}
                  {iframeError && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                      }}
                    >
                      <Text type="danger">Не удалось загрузить сайт</Text>
                      <br />
                      <Button
                        type="link"
                        href={getDirectUrl(currentItem)}
                        target="_blank"
                        style={{ marginTop: 8 }}
                      >
                        Открыть в новой вкладке
                      </Button>
                    </div>
                  )}
                  <iframe
                    ref={iframeRef}
                    key={`main-${currentItem.id}-${forceProxy ? 'proxy' : 'direct'}-${isolatedMode ? 'isolated' : 'normal'}`}
                    src={(() => {
                      const preloaded = preloadedIframes[currentItem.id]
                      // * If preloaded and blocked, or forceProxy is set, use proxy URL
                      if (preloaded?.blocked || forceProxy) {
                        const directUrl = getDirectUrl(currentItem)
                        const encodedUrl = encodeURIComponent(directUrl || '')
                        return `${import.meta.env.VITE_API_URL || '/api'}/sources/proxy-site?url=${encodedUrl}`
                      }
                      // * Otherwise use preloaded URL or fallback to iframeUrl
                      return preloaded?.url || iframeUrl
                    })()}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: forceProxy ? '4px solid #ff6b6b' : 'none',
                      display: (iframeLoaded || preloadedIframes[currentItem.id]?.loaded) && !checkingIframe ? 'block' : 'none',
                      boxShadow: forceProxy ? '0 0 0 2px #ff4757, inset 0 0 20px rgba(255, 107, 107, 0.1)' : 'none',
                    }}
                    onLoad={handleIframeLoad}
                    onError={handleIframeError}
                    sandbox={isolatedMode ? 'allow-scripts' : 'allow-scripts allow-same-origin'}
                  />
                </>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                  }}
                >
                  <Text type="secondary">Не удалось определить URL для предпросмотра</Text>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* * Compact action buttons - smaller for more iframe space */}
        <Card
          style={{
            borderRadius: 0,
            borderLeft: 0,
            borderRight: 0,
            borderBottom: 0,
            padding: '6px 12px',
          }}
          bodyStyle={{ padding: '6px 12px' }}
        >
          <Row justify="center" gutter={[12, 0]} align="middle">
            <Col>
              <Button
                type="primary"
                danger
                size="small"
                onClick={handleReject}
                disabled={isProcessingSwipe}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  padding: 0,
                  border: 'none',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                }}
                title="Отклонить (←)"
              >
                ✖️
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                size="small"
                onClick={handleUncertain}
                disabled={isProcessingSwipe}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  padding: 0,
                  background: '#faad14',
                  borderColor: '#faad14',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                }}
                title="Сомневаюсь (↑)"
              >
                🤔
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                size="small"
                onClick={handleApprove}
                disabled={isProcessingSwipe}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  padding: 0,
                  background: '#52c41a',
                  borderColor: '#52c41a',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                }}
                title="Одобрить (→)"
              >
                ❤️
              </Button>
            </Col>
          </Row>
        </Card>
      </div>

      {/* * Status modal (rendered via statusModal variable above early returns) */}
      {statusModal}

      {/* * Rejection modal */}
      <Modal
        title="Отклонение источника"
        open={rejectModalVisible}
        onCancel={() => {
          setRejectModalVisible(false)
          setPendingRejection(null)
          setCustomReasonMode(false)
          setCustomReason('')
        }}
        footer={null}
        width={500}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card size="small" style={{ background: '#fafafa' }}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Источник:</Text>
              <Text strong style={{ fontSize: 16 }}>{pendingRejection?.displayUrl}</Text>
            </Space>
          </Card>

          <div>
            <Text strong style={{ marginBottom: 12, display: 'block' }}>
              Причина отклонения:
            </Text>
            
            {!customReasonMode ? (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Button
                  type="default"
                  size="large"
                  block
                  onClick={() => {
                    setRejectModalVisible(false)
                    swipeAction('rejected', 'Не подходит')
                    setPendingRejection(null)
                  }}
                  style={{ 
                    textAlign: 'left', 
                    height: 'auto',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12
                  }}
                >
                  <span style={{ color: '#ff4d4f', fontSize: 18 }}>❌</span>
                  <Text strong style={{ flex: 1 }}>Не подходит</Text>
                </Button>

                <Button
                  type="default"
                  size="large"
                  block
                  onClick={() => {
                    setRejectModalVisible(false)
                    swipeAction('rejected', 'Широкое гео')
                    setPendingRejection(null)
                  }}
                  style={{ 
                    textAlign: 'left',
                    height: 'auto',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12
                  }}
                >
                  <span style={{ color: '#faad14', fontSize: 18 }}>⚠️</span>
                  <Text strong style={{ flex: 1 }}>Широкое гео</Text>
                </Button>

                <Button
                  type="default"
                  size="large"
                  block
                  onClick={() => {
                    setRejectModalVisible(false)
                    swipeAction('rejected', 'Источник не открылся')
                    setPendingRejection(null)
                  }}
                  style={{ 
                    textAlign: 'left',
                    height: 'auto',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12
                  }}
                >
                  <span style={{ color: '#8c8c8c', fontSize: 18 }}>🚫</span>
                  <Text strong style={{ flex: 1 }}>Источник не открылся</Text>
                </Button>

                <Button
                  type="default"
                  size="large"
                  block
                  onClick={() => setCustomReasonMode(true)}
                  style={{ 
                    textAlign: 'left',
                    height: 'auto',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    borderStyle: 'dashed'
                  }}
                >
                  <span style={{ color: '#1890ff', fontSize: 18 }}>✏️</span>
                  <Text strong style={{ flex: 1 }}>Другое (свободная форма)</Text>
                </Button>
              </Space>
            ) : (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Button
                  type="text"
                  size="small"
                  onClick={() => {
                    setCustomReasonMode(false)
                    setCustomReason('')
                  }}
                  style={{ padding: 0, height: 'auto' }}
                >
                  ← Назад к выбору
                </Button>
                
                <Input.TextArea
                  rows={4}
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Укажите причину отклонения..."
                  autoFocus
                  style={{ marginTop: 8 }}
                />

                <Button
                  type="primary"
                  size="large"
                  block
                  onClick={() => {
                    if (!customReason.trim()) {
                      message.error('Укажите причину отклонения')
                      return
                    }
                    setRejectModalVisible(false)
                    swipeAction('rejected', customReason.trim())
                    setPendingRejection(null)
                    setCustomReasonMode(false)
                    setCustomReason('')
                  }}
                  disabled={!customReason.trim()}
                >
                  Отклонить с этой причиной
                </Button>
              </Space>
            )}
          </div>

          <Button onClick={() => {
            setRejectModalVisible(false)
            setPendingRejection(null)
            setCustomReasonMode(false)
            setCustomReason('')
          }} block>
            Отмена
          </Button>
        </Space>
      </Modal>
    </div>
  )
}

export default SwipeModePage

